function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { Tooltip, Badge } from 'seagull';
import { ServiceAdvancedSettings } from "../../../../components/ServiceAdvancedSettings";
import { useIoContext } from "../../../../contexts";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { AgreementDataModel, serviceModel } from "../../../../models";
import { useValidateService } from "../../../../models/service/hooks/useValidateService";
import { getServiceDescriptionBadges } from "../../../../utils/service-description/getServiceDescriptionBadges";
import EditableTableCell from "../EditableTableCell/EditableTableCell";
import styles from "./AdvancedSettingsCell.module.scss";
import { jsx as _jsx } from "react/jsx-runtime";
function AdvancedSettingsModalContent(_ref) {
  let {
    serviceId,
    draft,
    wasSubmitted,
    setDraft
  } = _ref;
  const featureFlags = useFeatureFlags();
  const service = useMemo(() => draft.service(serviceId), [draft, serviceId]);
  function changeService(transformer) {
    setDraft(prev => prev.setService(serviceId, transformer));
  }
  return service ? /*#__PURE__*/_jsx("div", {
    className: styles.modalContent,
    children: /*#__PURE__*/_jsx(ServiceAdvancedSettings, {
      isFreshlyAdded: false,
      isOpenInitially: true,
      hideTitle: true,
      service: service,
      testId: "adv-settings-modal",
      showServiceValidationWarning: wasSubmitted,
      relationshipState: {
        type: 'awaitingApproval',
        effectiveDate: draft.effectiveDateAsDate()
      },
      isReadOnly: false,
      onPausedChange: (_, paused) => changeService(s => serviceModel.setPaused(s, paused)),
      onBillOnAcceptanceChange: (_, billOnAcceptance) => changeService(s => serviceModel.setSkipBillingOnAcceptance(s, !billOnAcceptance, featureFlags)),
      onProratedChange: (_, prorated) => changeService(s => serviceModel.setProrated(s, prorated)),
      onOptionalChange: (_, optional, includedByDefault) => changeService(s => serviceModel.setParticipation(s, optional, includedByDefault)),
      onLimitBillingTimesChange: (_, enabled, limit) => changeService(s => serviceModel.setMaxCharges(s, enabled, limit)),
      onAutoPriceIncreaseChange: (_, priceIncrease) => changeService(s => serviceModel.setPriceIncrease(s, priceIncrease)),
      onQboClassChange: (_, integratedClass) => changeService(s => serviceModel.setIntegratedClass(s, integratedClass)),
      onKarbonChange: (_, karbon, options) => changeService(s => serviceModel.setKarbonIntegration(s, karbon, options)),
      onFinancialCentsChange: (_, financialCents, options) => changeService(s => serviceModel.setFinancialCentsIntegration(s, financialCents, options)),
      onLimitedTimeDiscountOfferChange: (_, limitedTimeDiscountOffer) => {
        changeService(s => serviceModel.setLimitedTimeDiscountOffer(s, limitedTimeDiscountOffer));
      },
      onDiscountPeriodExpirationChange: (_, discountPeriodExpiration) => {
        changeService(s => serviceModel.setDiscountPeriodExpiration(s, discountPeriodExpiration));
      },
      onPreapprovedAmountChange: (_, amount, controlledByAdvancedSettings) => {
        changeService(s => serviceModel.setPreapprovedAmount(s, amount, controlledByAdvancedSettings));
      },
      onStartBillingAtChange: (_, startBillingAt) => {
        changeService(s => serviceModel.setStartBillingAt(s, startBillingAt));
      },
      onServiceChange: updatedService => {
        changeService(_s => updatedService);
      }
    })
  }) : null;
}
export default function AdvancedSettingsCell(props) {
  const {
    service,
    draft,
    onOpenModal,
    isDisabled,
    disabledTooltip
  } = props;
  const {
    getNow
  } = useIoContext();
  const featureFlags = useFeatureFlags();
  const {
    isInvalid
  } = useValidateService();
  const badgesList = useMemo(() => getServiceDescriptionBadges(service, AgreementDataModel.fromDraft(draft), getNow(), featureFlags, {
    hideDiscountBadge: true
  }), [draft, service]);
  const handleEdit = () => {
    onOpenModal({
      width: '684px',
      renderContent: p => ({
        title: 'Service advanced settings',
        testid: 'advanced-settings-modal',
        content: /*#__PURE__*/_jsx(AdvancedSettingsModalContent, _objectSpread(_objectSpread({}, p), {}, {
          serviceId: service.id
        })),
        onSubmit: defaultBehavior => {
          defaultBehavior();
        }
      }),
      shouldSubmit: async editedDraft => {
        const editedService = editedDraft.service(service.id);
        if (!editedService) return Promise.resolve(false);
        const isServiceInvalid = isInvalid(editedService, {
          relationshipEffectiveDate: editedDraft.effectiveDateAsDate(),
          isTemplate: false
        });
        return Promise.resolve(!isServiceInvalid);
      }
    });
  };
  return /*#__PURE__*/_jsx(EditableTableCell, {
    className: styles.root,
    onClick: handleEdit,
    testid: "edit-advanced-settings-".concat(service.id),
    isDisabled: isDisabled,
    disabledTooltip: disabledTooltip,
    children: /*#__PURE__*/_jsx("div", {
      className: styles.badges,
      children: badgesList.map(_ref2 => {
        let {
          text,
          intent,
          skin,
          tooltip
        } = _ref2;
        return /*#__PURE__*/_jsx(Tooltip, {
          title: tooltip || '',
          placement: "top",
          children: /*#__PURE__*/_jsx(Badge, {
            rounded: true,
            outlined: true,
            skin: skin,
            "data-testid": "advanced-settings-cell-badge-".concat(intent),
            children: text
          })
        }, intent);
      })
    })
  });
}